"use client";

import React, { useEffect, useRef } from "react";

export type VisualizerMode = "bars" | "wave" | "circle";

// Exported so PlayPage can set up Web Audio during the user gesture
export interface AudioNodes {
  ctx: AudioContext;
  analyser: AnalyserNode;
}

const audioNodeCache = new WeakMap<HTMLAudioElement, AudioNodes>();

export function connectAudio(el: HTMLAudioElement): AudioNodes | null {
  if (audioNodeCache.has(el)) {
    const cached = audioNodeCache.get(el)!;
    console.log('[Viz] connectAudio: cached nodes reused, ctx.state=', cached.ctx.state);
    if (cached.ctx.state === "suspended") cached.ctx.resume().catch(() => {});
    return cached;
  }
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) { console.error('[Viz] connectAudio: AudioContext not supported'); return null; }
    const ctx = new AudioCtx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.96;

    // Prefer captureStream() — does NOT require CORS headers on the stream.
    // Works on Chrome / Firefox / Edge. Not available on Safari.
    const rawCapture: ((fps?: number) => MediaStream) | undefined =
      (el as any).captureStream ?? (el as any).mozCaptureStream;

    let usedCaptureStream = false;
    if (rawCapture) {
      try {
        const stream = rawCapture.call(el);
        const tracks = stream.getAudioTracks();
        console.log('[Viz] captureStream() stream obtained, audioTracks:', tracks.length,
          tracks.map((t: MediaStreamTrack) => `${t.kind}/${t.readyState}/enabled:${t.enabled}`).join(', ') || '(none)');
        const src = ctx.createMediaStreamSource(stream);
        src.connect(analyser);
        // Do NOT connect analyser → ctx.destination here: audio already plays
        // through the element's own native pipeline.
        usedCaptureStream = true;
        console.log('[Viz] connectAudio: captureStream() OK — no CORS needed');
      } catch (e) {
        console.warn('[Viz] captureStream() failed, falling back to MediaElementSource:', e);
      }
    } else {
      console.log('[Viz] captureStream not available on this browser (expected on Safari/iOS)');
    }

    if (!usedCaptureStream) {
      // Safari fallback: MediaElementSource hijacks audio route through Web Audio.
      // Requires the server to return Access-Control-Allow-Origin on the stream URL.
      // Without CORS headers the analyser will return all zeros.
      const src = ctx.createMediaElementSource(el);
      src.connect(analyser);
      analyser.connect(ctx.destination); // must connect or audio goes silent
      console.log('[Viz] connectAudio: MediaElementSource fallback (CORS required for data)');
    }

    const nodes = { ctx, analyser };
    audioNodeCache.set(el, nodes);
    console.log('[Viz] connectAudio: NEW nodes, ctx.state=', ctx.state, 'crossOrigin=', el.crossOrigin, 'captureStream=', usedCaptureStream);
    ctx.resume().catch((e) => console.warn('[Viz] ctx.resume() failed:', e));
    ctx.onstatechange = () => console.log('[Viz] AudioContext state changed to:', ctx.state);
    return nodes;
  } catch(e) {
    console.error('[Viz] connectAudio ERROR:', e);
    return null;
  }
}

interface RadioVisualizerProps {
  analyser: AnalyserNode | null;
  isPlaying: boolean;
  isDarkMode: boolean;
  mode: VisualizerMode;
}

function getPrimary(): string {
  const v = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  return v ? `hsl(${v})` : "#7c3aed";
}

export default function RadioVisualizer({ analyser, isPlaying, isDarkMode, mode }: RadioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const frameRef = useRef(0);

  // Refs so the single RAF loop always reads current values without restarting
  const isPlayingRef = useRef(isPlaying);
  const modeRef = useRef(mode);
  const analyserRef = useRef<AnalyserNode | null>(analyser);

  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  useEffect(() => { analyserRef.current = analyser; }, [analyser]);

  // Single long-lived RAF loop — started once on mount, reads refs each frame
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const freqBuf = new Uint8Array(128);
    const timeBuf = new Uint8Array(256);
    // Per-bar smoothed values — interpolated each frame for fluid motion
    const COUNT = 28;
    const smoothBars = new Float32Array(COUNT).fill(0.06);

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      frameRef.current += 1;
      const f = frameRef.current;
      const playing = isPlayingRef.current;
      const m = modeRef.current;
      const W = canvas.width;
      const H = canvas.height;
      const color = getPrimary();

      // Read analyser data; if all zeros (suspended/no audio) treat as dead
      let realFreqData: Uint8Array | null = null;
      let realTimeData: Uint8Array | null = null;
      const an = analyserRef.current;
      if (an) {
        // Resume AudioContext if suspended (iOS Safari requires this every time)
        const actx = an.context as AudioContext;
        if (actx.state === 'suspended') actx.resume().catch(() => {});

        an.getByteFrequencyData(freqBuf);
        an.getByteTimeDomainData(timeBuf);
        const maxFreq = freqBuf.reduce((a, b) => Math.max(a, b), 0);
        // time-domain: 128 = silence; any deviation means audio is present
        const maxTimeDev = timeBuf.reduce((a, b) => Math.max(a, Math.abs(b - 128)), 0);

        // Debug: log every ~60 frames (~1s at 60fps)
        if (f % 60 === 0) {
          const el2 = (an as any).context?._sourceElement as HTMLAudioElement | undefined;
          console.log(
            '[Viz] RAF tick — ctx.state=', actx.state,
            '| maxFreq=', maxFreq,
            '| maxTimeDev=', maxTimeDev,
            '| freqBuf[0..7]=', Array.from(freqBuf.slice(0, 8)).join(','),
            '| timeBuf[0..7]=', Array.from(timeBuf.slice(0, 8)).join(','),
            '| playing=', isPlayingRef.current,
          );
        }

        if (maxFreq >= 1) {
          realFreqData = freqBuf;
          realTimeData = timeBuf;
        }
      } else if (f % 60 === 0) {
        console.log('[Viz] RAF tick — NO analyser, playing=', isPlayingRef.current);
      }

      ctx.clearRect(0, 0, W, H);

      if (m === "bars") drawBars(ctx, W, H, color, playing, realFreqData, smoothBars);
      else if (m === "wave") drawWave(ctx, W, H, color, playing, realTimeData);
      else drawCircle(ctx, W, H, color, playing, realFreqData);
    };

    loop();
    return () => cancelAnimationFrame(rafRef.current);
  // Run once on mount only
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const height = mode === "circle" ? 100 : 56;

  return (
    <canvas
      ref={canvasRef}
      width={320}
      height={height}
      className="w-full"
      style={{ height: `${height}px`, display: "block" }}
    />
  );
}

// ─── Draw helpers — only animate when real data present, idle otherwise ────────

function drawBars(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  color: string,
  playing: boolean,
  data: Uint8Array | null,
  smooth: Float32Array   // persists across frames for lerp
) {
  const COUNT = smooth.length;
  const gap = 3;
  const barW = (W - gap * (COUNT - 1)) / COUNT;
  // Lerp factor: higher = snappier, lower = smoother
  const lerpSpeed = data ? 0.06 : 0.03;

  for (let i = 0; i < COUNT; i++) {
    const target = data ? data[Math.floor((i / COUNT) * data.length)] / 255 : 0.06;
    smooth[i] += (target - smooth[i]) * lerpSpeed;
    const value = smooth[i];

    const barH = Math.max(2, value * H * 0.88);
    const x = i * (barW + gap);
    const y = (H - barH) / 2;
    const r = Math.min(barW / 2, 3);

    ctx.globalAlpha = data ? 0.6 + value * 0.4 : (playing ? 0.22 : 0.12);
    ctx.fillStyle = color;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x, y, barW, barH, r);
    else ctx.rect(x, y, barW, barH);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawWave(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  color: string,
  playing: boolean,
  data: Uint8Array | null,
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2.5;
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.globalAlpha = data ? 0.85 : (playing ? 0.22 : 0.12);

  const POINTS = data ? data.length : 64;
  const pts: [number, number][] = [];
  for (let i = 0; i < POINTS; i++) {
    const y = data
      ? ((data[i] - 128) / 128) * (H * 0.44) + H / 2
      : H / 2;
    pts.push([(i / (POINTS - 1)) * W, y]);
  }

  // Smooth curve through points using midpoint quadratic bezier
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  for (let i = 0; i < pts.length - 1; i++) {
    const mx = (pts[i][0] + pts[i + 1][0]) / 2;
    const my = (pts[i][1] + pts[i + 1][1]) / 2;
    ctx.quadraticCurveTo(pts[i][0], pts[i][1], mx, my);
  }
  ctx.lineTo(pts[pts.length - 1][0], pts[pts.length - 1][1]);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

function drawCircle(
  ctx: CanvasRenderingContext2D,
  W: number, H: number,
  color: string,
  playing: boolean,
  data: Uint8Array | null,
) {
  const cx = W / 2;
  const cy = H / 2;
  const BASE_R = Math.min(W, H) * 0.22;
  const COUNT = 64;

  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = data ? 0.28 : (playing ? 0.18 : 0.1);
  ctx.beginPath();
  ctx.arc(cx, cy, BASE_R, 0, Math.PI * 2);
  ctx.stroke();

  for (let i = 0; i < COUNT; i++) {
    const angle = (i / COUNT) * Math.PI * 2 - Math.PI / 2;
    const value = data
      ? data[Math.floor((i / COUNT) * data.length)] / 255
      : 0.05;

    const len = value * BASE_R * 0.95;
    const x1 = cx + Math.cos(angle) * BASE_R;
    const y1 = cy + Math.sin(angle) * BASE_R;
    const x2 = cx + Math.cos(angle) * (BASE_R + len);
    const y2 = cy + Math.sin(angle) * (BASE_R + len);

    ctx.strokeStyle = color;
    ctx.lineWidth = 2;
    ctx.globalAlpha = data ? 0.45 + value * 0.55 : (playing ? 0.14 : 0.08);
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

